import { pdkAxios } from '@/lib/pdk-axios';
import type { Deal } from '@/models/registry';
import { dealStore } from '@/models/stores';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { SelectSingleEventHandler } from 'react-day-picker';
import { Calendar } from '../../ui/calendar';

export type DeliveryDateComboboxProps = {
  delivery_date: Deal['delivery_date'];
  dealId: Deal['id'];
  afterOnChange?: () => void;
  onSuccess?: (
    data: Deal,
    variables: { delivery_date: Deal['delivery_date'] },
  ) => void;
  onError?: (
    error: Error,
    variables: { delivery_date: Deal['delivery_date'] },
  ) => void;
};

const DeliveryDateCombobox = ({
  delivery_date,
  dealId,
  afterOnChange,
  onSuccess,
  onError,
}: DeliveryDateComboboxProps) => {
  const queryClient = useQueryClient();
  const updateDeliveryDateMutation = useMutation({
    onMutate: async ({ delivery_date }) => {
      if (!dealId) return;

      dealStore.updateDeliveryDate({
        id: dealId,
        deliveryDate: delivery_date || null,
      });
    },
    mutationFn: async ({
      delivery_date,
    }: {
      delivery_date: Deal['delivery_date'];
    }) => {
      const response = await pdkAxios
        .post(`/deals/${dealId}/delivery-date`, { delivery_date })
        .catch((e) => {
          console.error('Error updating delivery date', e);
          throw e;
        });

      return response.data;
    },
    onSuccess: (data, variables) => {
      onSuccess?.(data, variables);
    },
    onError: (error, variables) => {
      console.error('Error updating delivery date', error, variables);

      onError?.(error, variables);
    },
    onSettled: () => {
      // DEV NOTE: invalidateQueries is cause a flicker bug
      // because the useQuery hook in useDeal re runs and gives back an old value
      // It may not even be refetching ...
      queryClient.invalidateQueries({ queryKey: ['deal', dealId] });
    },
  });

  const onSelectDate: SelectSingleEventHandler = (date: Date | undefined) => {
    updateDeliveryDateMutation.mutate({
      delivery_date: date?.toISOString() || null,
    });
  };

  const date = delivery_date ? new Date(delivery_date) : undefined;

  return (
    <div className="flex flex-row gap-3 items-center">
      <Calendar
        mode="single"
        selected={date}
        onSelect={(...rest) => {
          onSelectDate(...rest);
          afterOnChange?.();
        }}
        className="rounded-md border shadow-sm"
        defaultMonth={date}
      />
    </div>
  );
};

export default DeliveryDateCombobox;
